import {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
} from 'react';
import Keycloak from 'keycloak-js';
import { hideSplashScreen } from '~utils/splash';
import storage from '~utils/storage';
import config from '~constants/config';

type AuthStatus =
  | 'undetermined'
  | 'logging-in'
  | 'logging-out'
  | 'authenticated'
  | 'unauthenticated';

type AuthContextValue = {
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refetchToken: () => Promise<void>;
  userName: () => string;
  status: AuthStatus;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

let keycloakInstance: Keycloak | null = null;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('undetermined');
  const [authenticatedUser, setAuthenticatedUser] = useState<string>('-');

  const updateInstance = () => {
    if (!keycloakInstance) {
      keycloakInstance = new Keycloak({
        realm: 'cozify',
        clientId: 'portal-app',
        url: config.LOGIN_URL,
      });
    }
    keycloakInstance
      .init({
        onLoad: 'login-required',
        pkceMethod: 'S256',
        checkLoginIframe: false,
      })
      .then(authenticated => {
        if (authenticated && keycloakInstance?.token) {
          // The hub connection warms itself via `oneService.ensureConnected()`
          // (useTransportStatus / pushChannel) once the authenticated shell
          // mounts. Auth must NOT import the SignalR layer — doing so closes a
          // module cycle (oneService → cqrs → BaseApi → auth → signalR → oneService).
          setStatus('authenticated');
          setAuthenticatedUser(
            keycloakInstance.tokenParsed?.preferred_username ?? '-'
          );
        } else {
          setStatus('unauthenticated');
        }
      })
      .catch(() => setStatus('unauthenticated'));
  };

  useEffect(() => {
    updateInstance();
  }, []);

  async function logout() {
    setStatus('logging-out');
    await keycloakInstance?.logout();
    storage.clearAll();
    setStatus('unauthenticated');
  }

  async function refetchToken() {
    try {
      // Force a token refresh (passing -1 forces renewal regardless of expiry).
      // This is needed after tenant switch so the new token reflects the new
      // tenant context. Do NOT call updateInstance/init again — Keycloak only
      // allows a single init() per instance.
      await keycloakInstance?.updateToken(-1);
    } catch {
      setStatus('unauthenticated');
    }
  }

  async function checkAuth() {
    // setStatus('determining');

    // loadConfig();

    try {
      // const token = storage.get('@app/token');
      // if (token.accessToken) {
      //   setStatus('authenticated');
      // } else {
      //   await logout();
      // }
    } catch (error) {
      setStatus('unauthenticated');
    } finally {
      hideSplashScreen();
    }
  }

  function userName() {
    return authenticatedUser;
  }

  return (
    <AuthContext.Provider
      value={{ checkAuth, userName, status, logout, refetchToken }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('Missing AuthProvider!');
  return context;
};

export const useAuthCheck = () => {
  const { checkAuth, status } = useAuth();

  useEffect(() => {
    if (status === 'undetermined') {
      checkAuth();
    }
  }, []); // eslint-disable-line

  return status;
};

export const getToken = async function () {
  if (!keycloakInstance) {
    throw new Error('Missing AuthProvider!');
  }
  const keycloak = keycloakInstance;
  if (keycloak.isTokenExpired(200)) {
    try {
      await keycloak.updateToken(-1);
    } catch (e) {
      if (keycloak.isTokenExpired(10)) {
        throw e;
      }
    }
  }

  const token = keycloak.token ?? '';
  return token;
};
